// Add / remove attachments on any record that has an Attachments tab; one modal and one table drive them all.
import { baseApi } from './baseApi';
import { TAG_TYPES } from './tags';
import { processServerError } from './listQuery';
import { notifyOnSuccess } from './notifyOnSuccess';
import { buildAttachmentsFormData } from '@/lib/mappers/attachments';

export const ATTACHMENT_TARGETS = Object.freeze({
  contract: 'contract',
  shipment: 'shipment',
  declaration: 'declaration',
  allocation: 'allocation',
});

// Where each record's files live. Only an allocation removes by DELETE; the rest post `remove_file_ids`.
const TARGET_CONFIG = Object.freeze({
  [ATTACHMENT_TARGETS.contract]: {
    path: (module, id) => `/${module}/contracts/${id}/attachments`,
    tag: TAG_TYPES.Contract,
  },
  [ATTACHMENT_TARGETS.shipment]: {
    path: (module, id) => `/${module}/batch-group/${id}/attachments`,
    tag: TAG_TYPES.Shipment,
  },
  [ATTACHMENT_TARGETS.declaration]: {
    path: (module, id) => `/${module}/inbound/attachments/${id}`,
    tag: TAG_TYPES.Declaration,
  },
  [ATTACHMENT_TARGETS.allocation]: {
    path: (module, id) => `/${module}/pending-orders/fulfillments/${id}/attachments`,
    removePath: (module, id, fileId) =>
      `/${module}/pending-orders/fulfillments/${id}/attachments/${fileId}`,
    tag: TAG_TYPES.Allocation,
    // An allocation IS its outbound ledger transaction (same id), whose row reads `hasAttachments`.
    alsoTags: [TAG_TYPES.Transaction],
  },
});

export const targetTags = (result, error, { target, id }) => {
  const config = TARGET_CONFIG[target];
  return [config.tag, ...(config.alsoTags ?? [])].map((type) => ({ type, id }));
};

export const buildAddRequest = ({ target, module, id, billOfLading, invoice, otherDocuments }) => ({
  url: TARGET_CONFIG[target].path(module, id),
  method: 'POST',
  body: buildAttachmentsFormData({ billOfLading, invoice, otherDocuments }),
});

export const buildRemoveRequest = ({ target, module, id, fileId }) => {
  const config = TARGET_CONFIG[target];
  if (config.removePath) return { url: config.removePath(module, id, fileId), method: 'DELETE' };
  return {
    url: config.path(module, id),
    method: 'POST',
    body: buildAttachmentsFormData({ removeFileIds: [Number(fileId)] }),
  };
};

export const attachmentsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    addAttachments: build.mutation({
      query: buildAddRequest,
      transformErrorResponse: processServerError,
      onQueryStarted: notifyOnSuccess('ATTACHMENT_ADD_SUCCESS'),
      invalidatesTags: targetTags,
    }),

    removeAttachment: build.mutation({
      query: buildRemoveRequest,
      transformErrorResponse: processServerError,
      onQueryStarted: notifyOnSuccess('ATTACHMENT_REMOVE_SUCCESS'),
      invalidatesTags: targetTags,
    }),
  }),
});

export const { useAddAttachmentsMutation, useRemoveAttachmentMutation } = attachmentsApi;
